import { useState, useEffect, useRef, useCallback } from 'react';
import { logService } from '../services/logService';
import { databaseService } from '../services/database';
import { LogEntry, LogFilter, PCInstance, Network } from '../types/database';

interface LogsViewProps {
  roomId?: string;
}

const LEVEL_COLORS: Record<string, string> = {
  debug: '#6b7280',
  info: '#e2e8f0',
  warn: '#f59e0b',
  error: '#ef4444',
  fatal: '#dc2626',
};

const APP_NAMES = ['reality_agent', 'unreal', 'multiplayer_server', 'reality_operator'];
const LEVELS = ['debug', 'info', 'warn', 'error', 'fatal'];

export default function LogsView({ roomId }: LogsViewProps) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [liveTail, setLiveTail] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(true);

  // Filter state
  const [filterApp, setFilterApp] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterPC, setFilterPC] = useState('');
  const [filterNetwork, setFilterNetwork] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Reference data for dropdowns
  const [pcInstances, setPcInstances] = useState<PCInstance[]>([]);
  const [networks, setNetworks] = useState<Network[]>([]);

  const logsEndRef = useRef<HTMLDivElement>(null);
  const unsubRef = useRef<(() => void) | null>(null);
  const PAGE_SIZE = 100;

  // Load reference data for filter dropdowns
  useEffect(() => {
    const loadRefData = async () => {
      if (!roomId) return;
      try {
        const nets = await databaseService.getNetworksInRoom(roomId);
        setNetworks(nets);
        const pcs: PCInstance[] = [];
        for (const net of nets) {
          const instances = await databaseService.getPCInstancesInNetwork(net.id);
          pcs.push(...instances);
        }
        setPcInstances(pcs);
      } catch (err) {
        console.error('Failed to load reference data:', err);
      }
    };
    loadRefData();
  }, [roomId]);

  const buildFilter = useCallback((): LogFilter => {
    return {
      roomId: roomId || undefined,
      appName: filterApp || undefined,
      level: filterLevel || undefined,
      pcInstanceId: filterPC || undefined,
      networkId: filterNetwork || undefined,
      category: filterCategory || undefined,
      search: filterSearch || undefined,
      limit: PAGE_SIZE,
      offset: 0,
    };
  }, [roomId, filterApp, filterLevel, filterPC, filterNetwork, filterCategory, filterSearch]);

  // Load logs when filters change
  useEffect(() => {
    const loadLogs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const filter = buildFilter();
        const result = await logService.queryLogs(filter);
        setLogs(result);
        setHasMore(result.length >= PAGE_SIZE);
      } catch (err: any) {
        setError(`Failed to load logs: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    loadLogs();
  }, [buildFilter]);

  // Live tail subscription
  useEffect(() => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    if (!liveTail) return;

    const filter = buildFilter();
    const unsub = logService.subscribeToLogs(filter, (newLog) => {
      setLogs((prev) => [newLog, ...prev]);
    });
    unsubRef.current = unsub;

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [liveTail, buildFilter]);

  const loadMore = async () => {
    const filter = buildFilter();
    filter.offset = logs.length;
    try {
      const more = await logService.queryLogs(filter);
      setLogs((prev) => [...prev, ...more]);
      setHasMore(more.length >= PAGE_SIZE);
    } catch (err: any) {
      setError(`Failed to load more: ${err.message}`);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFilterSearch(searchInput);
  };

  const formatTimestamp = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }) + '.' + String(d.getMilliseconds()).padStart(3, '0');
  };

  const formatDate = (ts: string) => {
    return new Date(ts).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
    });
  };

  const getPCName = (pcId: string | null) => {
    if (!pcId) return '—';
    const pc = pcInstances.find((p) => p.id === pcId);
    return pc?.pc_name || pcId.slice(0, 8);
  };

  return (
    <div className="logs-view">
      {/* Filter Bar */}
      <div className="logs-filters">
        <select value={filterApp} onChange={(e) => setFilterApp(e.target.value)}>
          <option value="">All Apps</option>
          {APP_NAMES.map((app) => (
            <option key={app} value={app}>{app.replace(/_/g, ' ')}</option>
          ))}
        </select>

        <select value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)}>
          <option value="">All Levels</option>
          {LEVELS.map((lvl) => (
            <option key={lvl} value={lvl}>{lvl.toUpperCase()}</option>
          ))}
        </select>

        <select value={filterNetwork} onChange={(e) => { setFilterNetwork(e.target.value); setFilterPC(''); }}>
          <option value="">All Networks</option>
          {networks.map((net) => (
            <option key={net.id} value={net.id}>{net.name}</option>
          ))}
        </select>

        <select value={filterPC} onChange={(e) => setFilterPC(e.target.value)}>
          <option value="">All PCs</option>
          {pcInstances
            .filter((pc) => !filterNetwork || pc.network_id === filterNetwork)
            .map((pc) => (
              <option key={pc.id} value={pc.id}>{pc.pc_name || pc.id.slice(0, 8)}</option>
            ))}
        </select>

        <input
          type="text"
          placeholder="Category..."
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="logs-filter-input"
          style={{ width: '120px' }}
        />

        <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '4px' }}>
          <input
            type="text"
            placeholder="Search messages..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="logs-filter-input"
          />
          <button type="submit" className="logs-btn-sm">Search</button>
        </form>

        <button
          className={`logs-btn-sm ${liveTail ? 'logs-btn-active' : ''}`}
          onClick={() => setLiveTail(!liveTail)}
        >
          {liveTail ? 'Stop Live' : 'Live Tail'}
        </button>
      </div>

      {error && <div className="logs-error">{error}</div>}

      {/* Log Table */}
      <div className="logs-table-container">
        <table className="logs-table">
          <thead>
            <tr>
              <th style={{ width: '60px' }}>Date</th>
              <th style={{ width: '100px' }}>Time</th>
              <th style={{ width: '100px' }}>PC</th>
              <th style={{ width: '110px' }}>App</th>
              <th style={{ width: '90px' }}>Category</th>
              <th style={{ width: '60px' }}>Level</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr
                key={log.id}
                className={`logs-row logs-level-${log.level}`}
                onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                style={{ cursor: log.metadata ? 'pointer' : 'default' }}
              >
                <td className="logs-cell-date">{formatDate(log.timestamp)}</td>
                <td className="logs-cell-time">{formatTimestamp(log.timestamp)}</td>
                <td className="logs-cell-pc">{getPCName(log.pc_instance_id)}</td>
                <td className="logs-cell-app">{log.app_name.replace(/_/g, ' ')}</td>
                <td className="logs-cell-category">{log.category || '—'}</td>
                <td>
                  <span
                    className="logs-level-badge"
                    style={{
                      color: LEVEL_COLORS[log.level] || '#e2e8f0',
                      fontWeight: log.level === 'error' || log.level === 'fatal' ? 'bold' : 'normal',
                    }}
                  >
                    {log.level.toUpperCase()}
                  </span>
                </td>
                <td className="logs-cell-message">{log.message}</td>
              </tr>
            ))}
            {expandedLogId && (() => {
              const log = logs.find((l) => l.id === expandedLogId);
              if (!log?.metadata) return null;
              return (
                <tr className="logs-row-expanded">
                  <td colSpan={7}>
                    <pre className="logs-metadata">
                      {JSON.stringify(log.metadata, null, 2)}
                    </pre>
                  </td>
                </tr>
              );
            })()}
          </tbody>
        </table>

        {isLoading && <div className="logs-loading">Loading logs...</div>}

        {!isLoading && logs.length === 0 && (
          <div className="logs-empty">No logs found matching the current filters.</div>
        )}

        {hasMore && !isLoading && logs.length > 0 && (
          <button className="logs-load-more" onClick={loadMore}>
            Load more
          </button>
        )}

        <div ref={logsEndRef} />
      </div>
    </div>
  );
}
